import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService, PermissionCombinator } from 'src/app/auth';

export interface TreeNode {
  label: string;
  children?: TreeNode[];
  expanded?: boolean;
  icon?: string;
  data?: any;
  path?: string[];
  permission?: PermissionCombinator;
  show?: boolean;
}

// Dauer der Ausklapp-/Einklapp-Animation (siehe tree-view.sass, expandIn/collapseOut).
// Muss mit der CSS-Animationsdauer uebereinstimmen, sonst wird die DOM-Node zu
// frueh entfernt (Animation wirkt abgeschnitten) oder zu spaet (kurzer Sprung
// zurueck in den offenen Zustand, bevor sie verschwindet).
const COLLAPSE_ANIMATION_MS = 220;

@Component({
  selector: 'tree-view',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, TreeView],
  templateUrl: './tree-view.html',
  styleUrl: './tree-view.sass'
})
export class TreeView implements OnInit {
  @Input() nodes: TreeNode[] = [];
  @Input() selectedNodeId: string | null = null;
  @Output() selectedNodeChange = new EventEmitter<string>();

  private readonly auth = inject(AuthService);

  // Knoten, deren Kinder-Container noch fuer die Zuklapp-Animation im DOM
  // bleiben, obwohl node.expanded schon false ist (aria-expanded etc. sollen
  // sofort umschalten, nur das Entfernen aus dem DOM wird verzoegert).
  private readonly closeTimeouts = new Map<TreeNode, ReturnType<typeof setTimeout>>();
  private readonly closingNodes = new Set<TreeNode>();

  ngOnInit() {
    // Initialize expanded state for all nodes
    this.initializeExpandedState(this.nodes);
    // Sichtbarkeit wird jetzt reaktiv in der Vorlage über showNode() geprüft (nicht mehr hier
    // einmalig gefiltert) – so passt sich das Menü an, sobald die Rechte (via /api/me) geladen sind.
  }

  private initializeExpandedState(nodes: TreeNode[]) {
    nodes.forEach(node => {
      if (node.expanded === undefined) {
        node.expanded = false;
      }
      if (node.children) {
        this.initializeExpandedState(node.children);
      }
    });
  }

  toggleNode(node: TreeNode) {
    if (!node.children || node.children.length === 0) return;

    const existingTimeout = this.closeTimeouts.get(node);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.closeTimeouts.delete(node);
    }

    if (node.expanded) {
      // Zuklappen: sofort als "nicht mehr expanded" markieren (Chevron, aria-expanded),
      // aber den Kinder-Container noch fuer die Dauer der collapseOut-Animation im DOM
      // belassen, sonst verschwindet er ohne Animation.
      node.expanded = false;
      this.closingNodes.add(node);
      this.closeTimeouts.set(node, setTimeout(() => {
        this.closingNodes.delete(node);
        this.closeTimeouts.delete(node);
      }, COLLAPSE_ANIMATION_MS));
    } else {
      this.closingNodes.delete(node);
      node.expanded = true;
    }
  }

  isClosing(node: TreeNode): boolean {
    return this.closingNodes.has(node);
  }

  // navigation itself is handled by the routerLink on the anchor
  selectNode(node: TreeNode) {
    if (node.children) return;
    this.selectedNodeId = node.label;
    // emit selection so parent TreeView or host can update
    this.selectedNodeChange.emit(node.label);
  }

  // Ein verschachteltes Kind-tree-view (fuer node.children) meldet seine Auswahl
  // hier. Ohne das Weiterreichen bleibt selectedNodeId nur lokal in dieser
  // Ebene gesetzt - beim naechsten Change-Detection-Durchlauf ueberschreibt die
  // eigene @Input()-Bindung (gespeist von der Elternebene, die von der Aenderung
  // nie erfahren hat) den Wert wieder mit dem alten Stand. Bei Sub-Items (zwei
  // Ebenen unter der Wurzel) fuehrte das dazu, dass .selected nie haengen blieb
  // - sichtbar wurde nur noch der normale Fokus-Ring nach dem Klick.
  onChildSelectedChange(id: string) {
    this.selectedNodeId = id;
    this.selectedNodeChange.emit(id);
  }

  showNode(node: TreeNode): boolean {
    if (!node.permission) return true;
    return this.auth.hasPermission(node.permission);
  }

  hasChildren(node: TreeNode) {
    if (node.children && node.children.length > 0)
      return true;
    return false;
  }

  isSelected(node: TreeNode): boolean {
    return this.selectedNodeId === node.label;
  }
}
